import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ArrowForward,
  CheckCircleOutline,
  Close,
  WorkspacePremiumOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  getMySubscriptions,
  isSubscriptionAccessActive,
} from "../../services/subscriptionService";
import { getLicenseFeature } from "./licenseFeatureCatalog";
import AuthorizationNotice from "./AuthorizationNotice";
import "./LicenseUpgradeDialog.css";

const LicenseUpgradeContext = createContext({ requestUpgrade: null });
const LICENSE_PATHS = new Set(["/licencias", "/mis-licencias"]);

const hasUsableSubscription = (subscriptions) =>
  subscriptions.some(
    (subscription) =>
      isSubscriptionAccessActive(subscription)
      || subscription.status === "past_due"
  );

export const useLicenseUpgrade = () => useContext(LicenseUpgradeContext);

export function LicenseUpgradeDialogProvider({ children }) {
  const { isAuthenticated, loading: authLoading, user, permissionError, hasPermission } = useAuth();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState({
    open: false,
    permission: null,
    leaveOnClose: false,
  });
  const [checkingLicense, setCheckingLicense] = useState(false);
  const [checkedLoginUserId, setCheckedLoginUserId] = useState(null);

  const requestUpgrade = useCallback((permission = null, options = {}) => {
    if (authLoading || !isAuthenticated || permissionError) return;
    setDialog((current) => {
      if (!permission && current.open && current.permission) return current;
      return {
        open: true,
        permission,
        leaveOnClose: Boolean(options.leaveOnClose),
      };
    });
  }, [authLoading, isAuthenticated, permissionError]);

  useEffect(() => {
    if (permissionError || (dialog.permission && hasPermission?.(dialog.permission))) {
      setDialog((current) => current.open ? { ...current, open: false } : current);
    }
  }, [permissionError, dialog.permission, hasPermission]);

  const closeDialog = useCallback(() => {
    const leaveOnClose = dialog.leaveOnClose;
    setDialog((current) => ({ ...current, open: false }));
    if (leaveOnClose) {
      navigate(location.key === "default" ? "/dashboard" : -1);
    }
  }, [dialog.leaveOnClose, location.key, navigate]);

  useEffect(() => {
    if (authLoading || isAuthenticated) return;
    setCheckedLoginUserId(null);
    setCheckingLicense(false);
    setDialog({ open: false, permission: null, leaveOnClose: false });
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    if (
      authLoading
      || permissionError
      || !isAuthenticated
      || user?.rol === "admin"
      || LICENSE_PATHS.has(location.pathname)
      || checkedLoginUserId === user?.id
    ) {
      setCheckingLicense(false);
      return undefined;
    }

    let active = true;
    setCheckingLicense(true);
    getMySubscriptions()
      .then((subscriptions) => {
        if (!active) return;
        if (!hasUsableSubscription(subscriptions)) {
          requestUpgrade();
        }
        setCheckedLoginUserId(user?.id);
      })
      .catch((error) => {
        console.error("No se pudo comprobar si el usuario tiene licencia:", error);
      })
      .finally(() => {
        if (active) setCheckingLicense(false);
      });

    return () => {
      active = false;
    };
  }, [
    authLoading,
    permissionError,
    checkedLoginUserId,
    isAuthenticated,
    location.pathname,
    requestUpgrade,
    user?.id,
    user?.rol,
  ]);

  useEffect(() => {
    if (!checkingLicense && !dialog.open) return undefined;
    document.body.dataset.licenseUpgradeDialogActive = "true";
    return () => {
      delete document.body.dataset.licenseUpgradeDialogActive;
    };
  }, [checkingLicense, dialog.open]);

  const contextValue = useMemo(() => ({ requestUpgrade }), [requestUpgrade]);
  const feature = dialog.permission
    ? getLicenseFeature(dialog.permission)
    : {
        title: "Desbloquea todo el potencial de Soccertycs",
        description:
          "No tienes una licencia activa. Elige el plan que mejor encaje con tu trabajo y empieza a utilizar las herramientas profesionales.",
        plans: ["Coach", "Analyst", "Club"],
        benefits: [
          "Estadísticas e informes avanzados",
          "Planificación y scouting",
          "Herramientas profesionales según tu perfil",
        ],
      };

  const openPlans = () => {
    const returnTo = `${location.pathname}${location.search}`;
    const required = dialog.permission
      ? `&required=${encodeURIComponent(dialog.permission)}`
      : "";
    try {
      sessionStorage.setItem(
        "licenseUpgradeReturn",
        JSON.stringify({ pathname: returnTo, scrollY: window.scrollY })
      );
    } catch (error) {
      console.warn("No se pudo guardar la posición de retorno:", error);
    }
    setDialog((current) => ({ ...current, open: false }));
    navigate(
      `/licencias?returnTo=${encodeURIComponent(returnTo)}${required}`
    );
  };

  return (
    <LicenseUpgradeContext.Provider value={contextValue}>
      {isAuthenticated && <AuthorizationNotice />}
      {children}
      <Dialog
        open={dialog.open && !authLoading && !permissionError}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        className="lu-modal"
        aria-labelledby="license-upgrade-dialog-title"
        aria-describedby="license-upgrade-dialog-description"
        PaperProps={{
          className: "lu-dialog",
          "data-theme": theme.palette.mode,
        }}
      >
        <div className="lu-topline">
          <span className="lu-brand">SOCCERTYCS <span>/ ACCESO PROFESIONAL</span></span>
          <IconButton className="lu-close" aria-label="Cerrar aviso de licencia" onClick={closeDialog}>
            <Close aria-hidden="true" />
          </IconButton>
        </div>
        <div className="lu-heading">
          <Box className="lu-symbol"><WorkspacePremiumOutlined aria-hidden="true" /></Box>
          <p className="lu-eyebrow">Licencia necesaria</p>
          <DialogTitle id="license-upgrade-dialog-title" className="lu-title">
            {feature.title}
          </DialogTitle>
        </div>
        <DialogContent className="lu-content">
          <p id="license-upgrade-dialog-description" className="lu-description">{feature.description}</p>
          {feature.benefits.length > 0 && <ul className="lu-benefits">
            {feature.benefits.map((benefit) => <li key={benefit}>
              <CheckCircleOutline aria-hidden="true" /><span>{benefit}</span>
            </li>)}
          </ul>}
          {feature.plans.length > 0 && <div className="lu-plans">
            <p>Disponible con</p>
            <ul>{feature.plans.map((plan) => <li key={plan}>{plan}</li>)}</ul>
          </div>}
        </DialogContent>
        <DialogActions className="lu-actions">
          <Button className="lu-back" onClick={closeDialog}>
            {dialog.leaveOnClose ? "Volver" : "Ahora no"}
          </Button>
          <Button
            variant="contained"
            className="lu-primary"
            endIcon={<ArrowForward />}
            onClick={openPlans}
          >
            Ver licencias
          </Button>
        </DialogActions>
      </Dialog>
    </LicenseUpgradeContext.Provider>
  );
}
